import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneFrame, DemoCaption } from './PhoneFrame';
import { FLAGS, monkeyIcon } from './assets';
import { TEAM_NAMES } from './data';

const K = 'caseStudies.worldCup.predict';

// Grading mirrors the real engine for the final: final-stage tiers
// (exact 10 / margin 7 / outcome 5), the 90-minute rule (regulation
// score 0-0), and the draw guard: on a drawn match a "correct margin"
// drops to the outcome tier, so a draw that isn't exact earns 5, not 7.
function gradeFinal(a, b) {
  if (a === 0 && b === 0) return { tier: 'tierExact', why: 'whyExact', pts: 10 };
  if (a === b) return { tier: 'tierOutcome', why: 'whyDraw', pts: 5 };
  return { tier: 'tierNone', why: 'whyNone', pts: 0 };
}

const Team = ({ team, lang }) => (
  <div className="gc-team">
    <div className="gc-logo"><img src={FLAGS[team]} alt="" /></div>
    <div className="gc-name">{TEAM_NAMES[lang][team]}</div>
  </div>
);

const PredictWidget = ({ lang }) => {
  const { t } = useTranslation();
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [byPet, setByPet] = useState(false);
  // { error } or { a, b, byPet, tier, why, pts } for the last submission
  const [result, setResult] = useState(null);
  const p2Ref = useRef(null);

  const submit = (a, b, pet) => {
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0 || a > 9 || b > 9) {
      setResult({ error: true });
      return;
    }
    setResult({ a, b, byPet: pet, ...gradeFinal(a, b) });
  };

  // Score boxes behave like the app's: one digit, yellow once filled,
  // focus hops to the second box after the first digit.
  const onType = (setter, isFirst) => (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
    setter(v);
    setByPet(false);
    if (isFirst && v !== '' && p2Ref.current) p2Ref.current.focus();
  };

  const letPetPredict = () => {
    // The real pets pick small plausible scores; so does this one.
    const a = Math.floor(Math.random() * 3);
    const b = Math.floor(Math.random() * 3);
    setP1(String(a));
    setP2(String(b));
    setByPet(true);
    submit(a, b, true);
  };

  const graded = result && !result.error;
  const ptsTxt = graded ? (result.pts > 0 ? `+${result.pts}` : String(result.pts)) : '';
  const petBadge = graded && result.byPet ? <img src={monkeyIcon} alt="" /> : null;

  return (
    <figure className="demo">
      <DemoCaption title={t(`${K}.title`)} note={t(`${K}.note`)}>
        <div className="rules-chips">
          {t(`${K}.rules`, { returnObjects: true }).map((r) => <span key={r}>{r}</span>)}
        </div>
      </DemoCaption>
      <PhoneFrame lang={lang}>
        <div className="pred-panel">
          <div className={graded ? 'gc-wrap-submitted' : undefined}>
            <div className={`gc${graded ? ' submitted' : ''}`}>
              <div className="gc-grid">
                <Team team="Spain" lang={lang} />
                <div className="gc-center">
                  <div className="pi">
                    <input
                      className={`score-select${p1 !== '' ? ' filled' : ''}`}
                      type="text" inputMode="numeric" pattern="[0-9]*" maxLength={1} placeholder="?" autoComplete="off"
                      id={`wc-p1-${lang}`} aria-label={t(`${K}.p1Label`)}
                      value={p1} onChange={onType(setP1, true)} onFocus={(e) => e.target.select()}
                    />
                    <span className="score-sep">-</span>
                    <input
                      ref={p2Ref}
                      className={`score-select${p2 !== '' ? ' filled' : ''}`}
                      type="text" inputMode="numeric" pattern="[0-9]*" maxLength={1} placeholder="?" autoComplete="off"
                      id={`wc-p2-${lang}`} aria-label={t(`${K}.p2Label`)}
                      value={p2} onChange={onType(setP2, false)} onFocus={(e) => e.target.select()}
                    />
                  </div>
                </div>
                <Team team="Argentina" lang={lang} />
              </div>
            </div>
            {graded && (
              // GameList footer strip: "your prediction" + points. In RTL the
              // app writes p2 - p1 inside an LTR bdi so p1 sits on the right.
              <div className="gc-foot">
                <span>
                  {t(`${K}.yourPrediction`)}{' '}
                  <bdi>{lang === 'he' ? `${result.b} - ${result.a}` : `${result.a} - ${result.b}`}</bdi>
                  {petBadge}
                </span>
                <span className="gc-foot-pts">{t(`${K}.points`)} <bdi>{ptsTxt}</bdi></span>
              </div>
            )}
          </div>
          <div className={`pet-row${byPet ? ' show' : ''}`}>
            <img src={monkeyIcon} alt="" />
            <span>{t(`${K}.petRow`)}</span>
          </div>
          <div className="pred-actions">
            <button className="pill go" type="button" onClick={() => submit(parseInt(p1, 10), parseInt(p2, 10), byPet)}>
              {t(`${K}.submit`)}
            </button>
            <button className="pill pet" type="button" title={t(`${K}.petTitle`)} onClick={letPetPredict}>
              <img src={monkeyIcon} alt="" />{t(`${K}.pet`)}
            </button>
          </div>
          <p className="pred-res" aria-live="polite">
            {result && result.error && t(`${K}.invalid`)}
            {graded && (
              <>
                <span className="tier">{t(`${K}.${result.tier}`)} · <b>{result.pts}+</b></span>
                <br />
                {`${t(`${K}.${result.byPet ? 'whoPet' : 'whoYou'}`)} ${result.a}-${result.b}. ${t(`${K}.${result.why}`)}`}
              </>
            )}
          </p>
        </div>
      </PhoneFrame>
    </figure>
  );
};

export default PredictWidget;
